/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetHiddenMarketCheckRow, GetHiddenMarketsRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String } from '../../helpers/index.js';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── useGetHiddenMarketCheck ────────────────────────────────────────────────────────────

export interface useGetHiddenMarketCheckArgs { readonly marketPubkey: string; readonly fields?: readonly ('hidden')[]; }


async function _useGetHiddenMarketCheckQueryFn(
  args: useGetHiddenMarketCheckArgs,
  ctx: QueryFunctionContext,
): Promise<GetHiddenMarketCheckRow | null> {
  if (args.marketPubkey === undefined || args.marketPubkey === null || args.marketPubkey === '') return null;
  const url = `/v1/hidden-markets/${args.marketPubkey}`;
  const params = new URLSearchParams();
  // FQ-DETAIL-SHARED-ENTRY-001 — fetch the full row; `args.fields` is
  // intentionally NOT forwarded (shared id-only cache entry).
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetHiddenMarketCheck',
    routeTemplate: '/v1/hidden-markets/{marketPubkey}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetHiddenMarketCheckRow;
}


const _useGetHiddenMarketCheckFactory = Object.assign(
  (args: useGetHiddenMarketCheckArgs) => ({
    queryKey: ['hidden-markets', 'detail', args.marketPubkey] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetHiddenMarketCheckQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetHiddenMarketCheck',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['hidden-markets', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetHiddenMarketCheckArgs, GetHiddenMarketCheckRow>;


export const useGetHiddenMarketCheckSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/hidden-markets/{marketPubkey}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetHiddenMarketCheckFactory },
  parseInput: (raw: unknown): useGetHiddenMarketCheckArgs => raw as useGetHiddenMarketCheckArgs,
  parseResponse: (raw: unknown): GetHiddenMarketCheckRow => raw as GetHiddenMarketCheckRow,
  specName: 'useGetHiddenMarketCheck' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  viewerRelationship: {"kind":"hidden","point":{"targetArg":"marketPubkey","valueField":"hidden"}} as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetHiddenMarketCheckRow>;


// ─── useGetHiddenMarkets ────────────────────────────────────────────────────────────

export interface useGetHiddenMarketsArgs { readonly viewportGated?: boolean; readonly fields?: readonly ('marketPubkey' | 'createdAt' | 'market' | 'fallbackLabel')[]; }


async function _useGetHiddenMarketsQueryFn(
  args: useGetHiddenMarketsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetHiddenMarketsRow>> {
  const url = '/v1/hidden-markets';
  const params = new URLSearchParams();
  const effectiveFields = args.fields === undefined ? undefined : [...new Set([...args.fields, 'marketPubkey'])].sort();
  if (effectiveFields !== undefined) params.set('fields', effectiveFields.join(','));
  params.set('limit', String(limitOverride ?? 25));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetHiddenMarkets',
    routeTemplate: '/v1/hidden-markets',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetHiddenMarketsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetHiddenMarketsFactory = Object.assign(
  (args: useGetHiddenMarketsArgs) => ({
    queryKey: ['hidden-markets', 'list', ((args.fields === undefined ? undefined : [...new Set([...args.fields, 'marketPubkey'])].sort()) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetHiddenMarketsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetHiddenMarkets',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['hidden-markets', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetHiddenMarketsArgs, PagedData<GetHiddenMarketsRow>>;


export const useGetHiddenMarketsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/hidden-markets',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetHiddenMarketsFactory },
  parseInput: (raw: unknown): useGetHiddenMarketsArgs => raw as useGetHiddenMarketsArgs,
  parseResponse: (raw: unknown): PagedData<GetHiddenMarketsRow> => raw as PagedData<GetHiddenMarketsRow>,
  specName: 'useGetHiddenMarkets' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  viewerRelationship: {"kind":"hidden","collection":{"rowTargetPath":"marketPubkey"}} as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetHiddenMarketsRow>>;
export const useGetHiddenMarketsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 25,
  maxPageSize: 100,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetHiddenMarketsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetHiddenMarketsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetHiddenMarketsPerEntitySubjects(_args: useGetHiddenMarketsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetHiddenMarketsRowSubjectTemplates(_args: useGetHiddenMarketsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetHiddenMarketsApplyEvent(
  prev: PagedData<GetHiddenMarketsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetHiddenMarketsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetHiddenMarketsRealtime = {
  domain: 'hidden-markets' as const,
  domainPins: [] as readonly string[],
  rowKey: 'marketPubkey' as const,
  perEntitySubjects: (args: useGetHiddenMarketsArgs) => _useGetHiddenMarketsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetHiddenMarketsArgs) => _useGetHiddenMarketsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetHiddenMarketsArgs) =>
    (prev: PagedData<GetHiddenMarketsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetHiddenMarketsRow> | undefined =>
      _useGetHiddenMarketsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetHiddenMarketsRow>, unknown>;
